import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import profilePic from '../assets/images/mystory3.jpg';
import { sendChatMessageMarathi } from '../apiService';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      text: "नमस्कार! मी डॉ. राजेंद्र शेंडे यांचे प्रतिनिधित्व करणारा एक AI चॅटबॉट आहे. मी पर्यावरण, शाश्वतता, SDGs, नेट झिरो आणि हवामान कृती या विषयांवर चर्चा करू शकतो. मी तुम्हाला कशी मदत करू शकतो?",
      sender: 'bot',
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const chatWindowRef = useRef(null);
  const chatIconRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Scroll to bottom of messages - ROBUST VERSION
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      // Use scrollTop for reliable scrolling
      const scrollHeight = messagesContainerRef.current.scrollHeight;
      messagesContainerRef.current.scrollTo({
        top: scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Scroll when messages change
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [messages]);

  // Scroll when chat opens
  useEffect(() => {
    if (isOpen) {
      // Delay to allow GSAP animation to complete
      const timeoutId = setTimeout(() => {
        scrollToBottom();
      }, 600);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  // GSAP animation for opening/closing chat
  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        chatWindowRef.current,
        {
          scale: 0,
          opacity: 0,
          y: 50,
          transformOrigin: 'bottom right'
        },
        {
          scale: 1,
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'back.out(1.7)'
        }
      );
      
      gsap.to(chatIconRef.current, {
        scale: 0,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in'
      });
    } else {
      if (chatWindowRef.current) {
        gsap.to(chatWindowRef.current, {
          scale: 0,
          opacity: 0,
          y: 50,
          duration: 0.3,
          ease: 'power2.in'
        });
      }
      
      gsap.to(chatIconRef.current, {
        scale: 1,
        opacity: 1,
        duration: 0.5,
        delay: 0.2,
        ease: 'back.out(1.7)'
      });
    }
  }, [isOpen]);

  // Pulse animation for chat icon
  useEffect(() => {
    if (!isOpen && chatIconRef.current) {
      gsap.to(chatIconRef.current, {
        scale: 1.1,
        duration: 1,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await sendChatMessageMarathi(inputMessage);
      
      const botMessage = {
        text: response.data.message,
        sender: 'bot',
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        text: 'क्षमस्व, मला त्रुटी आली. कृपया पुन्हा प्रयत्न करा.',
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Chat Icon Button */}
      <button
        ref={chatIconRef}
        onClick={() => setIsOpen(true)}
        className={`${
          isOpen ? 'hidden' : 'flex'
        } items-center justify-center w-16 h-16 rounded-full bg-red-600 shadow-2xl hover:shadow-red-600/50 transition-all duration-300 cursor-pointer`}
        aria-label="चॅट उघडा"
      >
        <svg
          className="w-8 h-8 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      </button>

      {/* Chat Window */}
      <div
        ref={chatWindowRef}
        className={`${
          isOpen ? 'block' : 'hidden'
        } w-[380px] h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-red-100`}
        style={{ transformOrigin: 'bottom right' }}
      >
        {/* Header */}
        <div className="bg-red-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={profilePic}
              alt="डॉ. राजेंद्र शेंडे"
              className="w-12 h-12 rounded-full border-2 border-white object-cover"
            />
            <div>
              <h3 className="text-white font-semibold text-lg">डॉ. राजेंद्र शेंडे</h3>
              <p className="text-red-100 text-xs">AI सहाय्यक</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors duration-200"
            aria-label="चॅट बंद करा"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Messages Container */}
        <div 
          ref={messagesContainerRef}
          className="chatbot-messages-scroll flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-red-50 to-white"
          style={{ 
            scrollBehavior: 'smooth',
            overflowY: 'scroll',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.sender === 'user' ? 'justify-end' : 'justify-start'
              } animate-fadeIn`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.sender === 'user'
                    ? 'bg-red-600 text-white rounded-br-md'
                    : 'bg-white text-gray-800 shadow-md border border-red-100 rounded-bl-md'
                }`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                  {message.text}
                </p>
                <span
                  className={`text-xs mt-1 block ${
                    message.sender === 'user' ? 'text-red-100' : 'text-gray-400'
                  }`}
                >
                  {new Date(message.timestamp).toLocaleTimeString('mr-IN', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white text-gray-800 shadow-md border border-red-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white border-t border-red-100">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="पर्यावरण, SDGs, शाश्वतता बद्दल विचारा..."
              maxLength={500}
              className="flex-1 px-4 py-3 rounded-xl border border-red-200 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm"
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="bg-red-600 text-white px-5 py-3 rounded-xl hover:bg-red-700 hover:shadow-lg hover:shadow-red-600/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
              aria-label="संदेश पाठवा"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            AI द्वारे संचालित • विषय: पर्यावरण, SDGs, नेट झिरो
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatBot;
